import React, { useEffect, useRef, useState } from "react"
import { Link, NavLink } from "react-router-dom"
import { BrandLogo } from "./BrandLogo"

const LINKS = [
  { to: "/dashboard", label: "Início" },
  { to: "/plano", label: "Plano" },
]

/** Casca comum das páginas (header + container). O engine do simulado NÃO usa isso. */
async function logout() {
  const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
  try {
    await fetch("/logout", {
      method: "DELETE",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": token ?? "", Accept: "application/json" },
    })
  } catch {
    // ignora falha de rede — redireciona de qualquer forma
  }
  window.location.href = "/"
}

function AccountMenu() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  const itemClass =
    "block w-full px-4 py-2.5 text-left text-sm font-medium text-navy-700 transition-colors hover:bg-slate-100"

  return (
    <div className="relative ml-1" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-navy-500 transition-colors hover:text-navy-900"
      >
        Conta
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1.5 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg shadow-slate-900/10"
        >
          <Link to="/configuracoes" role="menuitem" className={itemClass} onClick={() => setOpen(false)}>
            Configurações
          </Link>
          <Link to="/suporte" role="menuitem" className={itemClass} onClick={() => setOpen(false)}>
            Suporte
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              void logout()
            }}
            className={`${itemClass} border-t border-slate-100 text-red-600 hover:bg-red-50`}
          >
            Sair
          </button>
        </div>
      )}
    </div>
  )
}

export function AppShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-4xl items-center justify-between px-4">
          <BrandLogo />
          <nav className="flex items-center gap-1" aria-label="Navegação principal">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-200 text-gold-600" : "text-navy-500 hover:text-navy-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <AccountMenu />
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 pb-16 pt-6">{children}</main>
    </div>
  )
}
